/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Heading, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { SharestateInput } from '../../actions/newsletter-share-state.action';
import { _isJsonString } from '../utilities/helper';
import { ICIframeData } from './c-iframe.data';

import { IHeadingData, ICIframeProps } from './c-iframe.props.autogenerated';

export interface IIframeViewProps extends ICIframeProps<ICIframeData> {
    Heading: React.ReactNode;
    IframeContainer: IModuleProps;
    IFrame: React.ReactNode;
    alert?: React.ReactNode;
    cookieConsentAccepted?: boolean;
    showForm?: boolean;
    closeScriptModal(): void;
}

/**
 *
 * Iframe component.
 * @extends {React.PureComponent<ICIframeProps>}
 */
class Iframe extends React.PureComponent<ICIframeProps<ICIframeData>> {
    private readonly iframeRef: React.RefObject<HTMLIFrameElement>;

    constructor(props: ICIframeProps<ICIframeData>) {
        super(props);
        this.iframeRef = React.createRef();

        if (typeof window !== 'undefined') {
            window.addEventListener(
                'message',
                event => {
                    console.log(event);
                    if (event?.data?.type === 'closeNlModal') {
                        this._closeScriptModal();
                    }
                },
                false
            );
        }
    }

    public render(): JSX.Element | null {
        const { config, resources } = this.props;
        const isConsentGiven =
            this.props.context.request &&
            this.props.context.request.cookies &&
            this.props.context.request.cookies.isConsentGiven &&
            this.props.context.request.cookies.isConsentGiven();

        if (!(config && config.target && config.target.destinationUrl)) {
            return null;
        }

        let srcURL;
        try {
            srcURL = new URL(config.target.destinationUrl);
        } catch (error) {
            this.props.telemetry.exception(error);
            return null;
        }

        if (srcURL && srcURL.protocol && srcURL.protocol.toLocaleLowerCase() !== 'https:') {
            return null;
        }
        const iframeClassName = classnames('ms-iframe', config.className);

        let viewProps: IIframeViewProps = {
            ...(this.props as ICIframeProps<ICIframeData>),
            Heading: this._createHeading(config.heading!),
            IframeContainer: {
                moduleProps: this.props,
                className: iframeClassName
            },
            IFrame: this._createIframe(),
            showForm: this.props.data.newsletterShareState.result?.isModalOpen,
            closeScriptModal: this._closeScriptModal
        };

        if (!isConsentGiven) {
            viewProps = {
                ...viewProps,
                alert: this._renderAlert(resources.cookieConsentRequiredMessage)
            };
        } else {
            viewProps = {
                ...viewProps,
                cookieConsentAccepted: true
            };
        }
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _createHeading(heading: IHeadingData): React.ReactNode | null {
        if (!heading || !heading.text || heading.text.length === 0) {
            return null;
        }

        return <Heading {...heading} className='ms-iframe__heading' />;
    }

    private _renderAlert(message: string): React.ReactNode | null {
        return (
            <p className='ms-iframe__error-message' role='alert' aria-live='assertive'>
                {message}
            </p>
        );
    }

    private _createIframe(): React.ReactNode | null {
        const { config } = this.props;
        const targetURL = config.target.destinationUrl;
        const iframeStyle =
            config.iframeStyleOverride && _isJsonString(config.iframeStyleOverride)
                ? (JSON.parse(config.iframeStyleOverride) as React.CSSProperties)
                : {};

        return (
            <iframe
                id={'ms-script-iframe'}
                className={classnames('ms-iframe__content')}
                src={targetURL}
                height={'620px'}
                aria-label={config.iframeAriaLabel}
                ref={this.iframeRef}
                style={iframeStyle}
                frameBorder={config.frameBorder}
                marginHeight={config.marginHeight}
                marginWidth={config.marginWidth}
                allowFullScreen={config.allowFullScreen}
                allowTransparency={config.allowTransparency}
                scrolling={config.scrolling}
                seamless={config.seamless}
                width={config.width}
            />
        );
    }

    private _closeScriptModal = (): void => {
        if (this.props.data.newsletterShareState.result) {
            this.props.context.actionContext.update(new SharestateInput(), {
                ...this.props.data.newsletterShareState.result,
                isModalOpen: false,
                email: ''
            });
        }
    };
}

export default Iframe;
